/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/definition/shell 层；
 * 2. 负责定义抽壳特征参数真相对象；
 * 3. 抽壳定义对象只承载结构化参数，不执行任何几何逻辑。
 */

import { FeatureDefinition, type FeatureDefinitionSnapshot } from '../FeatureDefinition';

/**
 * 抽壳特征定义快照。
 */
export interface ShellFeatureDefinitionSnapshot extends FeatureDefinitionSnapshot {
    /** 当前特征种类。 */
    featureKind: 'shell';
    /** 原始命令参数。 */
    commandParams: ShellParams;
}

/**
 * 抽壳特征定义投影。
 */
export interface ShellFeatureDefinitionProjection {
    /** 上游实体对象 ID。 */
    upstreamBodyObjectId: string | null;
    /** 结果对象 ID。 */
    resultObjectId: string | null;
    /** 抽壳定义。 */
    definition: ShellFeatureDefinition;
    /** 特征名称。 */
    name: string | null;
}

/**
 * 抽壳特征定义对象。
 */
export class ShellFeatureDefinitionModel extends FeatureDefinition<ShellFeatureDefinitionSnapshot> {
    /**
     * 基于命令参数创建抽壳定义对象。
     *
     * @param input 输入参数。
     * @returns 抽壳定义对象。
     */
    public static fromParams(input: {
        commandParams: ShellParams;
        version?: number;
    }): ShellFeatureDefinitionModel {
        return new ShellFeatureDefinitionModel({
            featureKind: 'shell',
            version: input.version ?? 1,
            commandParams: structuredClone(input.commandParams),
        });
    }

    /**
     * @param snapshot 定义快照。
     */
    public constructor(snapshot: ShellFeatureDefinitionSnapshot) {
        super(snapshot);
    }

    /**
     * 获取原始命令参数。
     *
     * @returns 命令参数快照。
     */
    public getCommandParams(): ShellParams {
        return this.toSnapshot().commandParams;
    }

    /**
     * 获取抽壳领域定义。
     *
     * @returns 抽壳定义快照。
     */
    public getShellDefinition(): ShellFeatureDefinition {
        return structuredClone(this.toSnapshot().commandParams.definition);
    }

    /**
     * 获取上游实体对象 ID。
     *
     * @returns 上游实体对象 ID。
     */
    public getUpstreamBodyObjectId(): string | null {
        const value = this.toSnapshot().commandParams.upstreamBodyObjectId;
        return typeof value === 'string' ? value : null;
    }

    /**
     * 生成面向 UI 的投影快照。
     *
     * @returns 投影快照。
     */
    public toProjection(): ShellFeatureDefinitionProjection {
        const commandParams = this.toSnapshot().commandParams;
        return {
            upstreamBodyObjectId: typeof commandParams.upstreamBodyObjectId === 'string'
                ? commandParams.upstreamBodyObjectId
                : null,
            resultObjectId: typeof commandParams.resultObjectId === 'string'
                ? commandParams.resultObjectId
                : null,
            definition: structuredClone(commandParams.definition),
            name: typeof commandParams.name === 'string' ? commandParams.name : null,
        };
    }

    /**
     * 基于新命令参数生成下一份定义对象。
     *
     * @param commandParams 新命令参数。
     * @returns 新定义对象。
     */
    public withCommandParams(commandParams: ShellParams): ShellFeatureDefinitionModel {
        return ShellFeatureDefinitionModel.fromParams({
            commandParams,
            version: this.getVersion(),
        });
    }
}
